// Barcode scanning with the camera (CAT-C-06, PRD-04, ADR-092). The same code runs in the browser and in the APK's
// WebView: the camera comes from getUserMedia; codes are read by the platform BarcodeDetector when it exists, else by
// ZXing. A read returns digits only; the caller looks the code up (it selects the product and its unit).
import { BrowserMultiFormatReader, type IScannerControls } from '@zxing/browser';
import { BarcodeFormat, DecodeHintType } from '@zxing/library';

const FORMATS = ['ean_13', 'ean_8', 'upc_a', 'upc_e'] as const;

interface DetectedBarcode {
  rawValue: string;
}
interface BarcodeDetectorLike {
  detect(source: CanvasImageSource): Promise<DetectedBarcode[]>;
}
type DetectorClass = {
  new (options: { formats: readonly string[] }): BarcodeDetectorLike;
  getSupportedFormats(): Promise<string[]>;
};

export interface ScanSession {
  stop(): void;
}

function zxingReader(): BrowserMultiFormatReader {
  const hints = new Map();
  hints.set(DecodeHintType.POSSIBLE_FORMATS, [
    BarcodeFormat.EAN_13,
    BarcodeFormat.EAN_8,
    BarcodeFormat.UPC_A,
    BarcodeFormat.UPC_E,
  ]);
  return new BrowserMultiFormatReader(hints);
}

/** True when a camera API exists at all (it can still be refused). */
export function cameraAvailable(): boolean {
  return typeof navigator !== 'undefined' && Boolean(navigator.mediaDevices?.getUserMedia);
}

/**
 * Starts the back camera in `video` and calls `onCode` once with the first barcode read.
 * Rejects with 'CAMERA_UNAVAILABLE' when there is no camera or permission is refused.
 */
export async function startScan(video: HTMLVideoElement, onCode: (code: string) => void): Promise<ScanSession> {
  if (!cameraAvailable()) throw new Error('CAMERA_UNAVAILABLE');
  let stream: MediaStream;
  try {
    stream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: { ideal: 'environment' } },
      audio: false,
    });
  } catch {
    throw new Error('CAMERA_UNAVAILABLE');
  }
  video.srcObject = stream;
  video.setAttribute('playsinline', 'true');
  await video.play().catch(() => undefined);
  let stopped = false;
  let controls: IScannerControls | undefined;
  const stop = () => {
    stopped = true;
    controls?.stop();
    stream.getTracks().forEach((t) => t.stop());
    // Only detach our own stream: a newer session may already be showing in the same element.
    if (video.srcObject === stream) video.srcObject = null;
  };
  const found = (code: string) => {
    if (stopped) return;
    const digits = code.replace(/\D/g, '');
    if (!digits) return;
    stop();
    onCode(digits);
  };

  const Detector = (globalThis as { BarcodeDetector?: DetectorClass }).BarcodeDetector;
  const supported = Detector ? await Detector.getSupportedFormats().catch(() => [] as string[]) : [];
  if (Detector && FORMATS.some((f) => supported.includes(f))) {
    const detector = new Detector({ formats: FORMATS.filter((f) => supported.includes(f)) });
    const tick = async () => {
      if (stopped) return;
      try {
        const hits = await detector.detect(video);
        if (hits[0]) return found(hits[0].rawValue);
      } catch {
        // A frame that cannot be read yet (camera warming up): try the next one.
      }
      window.setTimeout(() => void tick(), 200);
    };
    void tick();
  } else {
    controls = await zxingReader().decodeFromVideoElement(video, (result) => {
      if (result) found(result.getText());
    });
  }
  return { stop };
}

/** Reads one barcode from a still image (a canvas or an image element). Used by tests and photo fallbacks. */
export async function readBarcodeFromCanvas(canvas: HTMLCanvasElement): Promise<string | null> {
  try {
    return zxingReader().decodeFromCanvas(canvas).getText();
  } catch {
    return null;
  }
}
